/**
 * Analytics API Client
 */

import { auth } from '../firebase';
import type {
  FunnelResponse,
  FunnelParams,
  DrilldownResponse,
  DrilldownParams,
  BottleneckResponse,
  SummaryApiResponse,
  DashboardConfig,
  DiagnosticResponse,
} from '@/types/analytics';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';
const QA_BYPASS = import.meta.env.VITE_QA_BYPASS === 'true';

// ---------------------------------------------------------------------------
// QA mock data — returned when VITE_QA_BYPASS=true (no backend needed)
// ---------------------------------------------------------------------------

const QA_BOTTLENECK: BottleneckResponse = {
  show_rate: { rate: 72.5, severity: 'yellow', data_quality: 'complete' },
  close_rate: { rate: 28.3, severity: 'green', data_quality: 'complete' },
  time_to_first_call: { median_minutes: 4.2, avg_minutes: 6.1, severity: 'green', data_quality: 'complete' },
  stale_leads: { count: 14, severity: 'yellow', data_quality: 'complete' },
  call_quality: {
    qualification_rate: 41.0,
    qualified_held_calls: 37,
    total_held_calls: 90,
    avg_duration_all: 4.8,
    severity: 'green',
    data_quality: 'complete',
  },
  stage_dropoffs: { showed: { drop_off_pct: 27.5 } },
  deal_velocity: { avg_days_lead_to_close: 18.2 },
} as unknown as BottleneckResponse;

const QA_SUMMARY: SummaryApiResponse = {
  summary: { cpl: 34.50, leads: 412, spend: 14214.00, campaigns: 5 },
} as unknown as SummaryApiResponse;

const QA_FUNNEL: FunnelResponse = {
  stages: [
    { stage: 'Leads', count: 412 },
    { stage: 'Called', count: 280 },
    { stage: 'Connected', count: 130 },
    { stage: 'Qualified', count: 53 },
    { stage: 'Closed', count: 15 },
  ],
  summary: { total_leads: 412, total_revenue: 87500, close_rate: 28.3 },
} as unknown as FunnelResponse;

const QA_DASHBOARD_CONFIG: DashboardConfig = {
  tier1_kpis: ['show_rate', 'close_rate', 'cpl', 'speed_to_lead', 'stale_leads', 'qualified_calls', 'revenue'],
} as unknown as DashboardConfig;

const QA_DIAGNOSTIC: DiagnosticResponse = {
  overall_health: 'yellow',
  bullets: [
    { severity: 'yellow', text: 'Show rate 72.5% — below 80% target' },
    { severity: 'green',  text: 'Close rate 28.3% — on track' },
    { severity: 'yellow', text: '14 stale leads need follow-up' },
    { severity: 'green',  text: 'Speed to lead 4.2 min — excellent' },
  ],
} as unknown as DiagnosticResponse;

// ---------------------------------------------------------------------------

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function buildQs(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      qs.set(k, String(v));
    }
  }
  return qs.toString();
}

export async function fetchFunnel(params: FunnelParams = {}): Promise<FunnelResponse> {
  if (QA_BYPASS) return QA_FUNNEL;
  const headers = await getAuthHeader();
  const qs = buildQs(params as Record<string, string>);
  const res = await fetch(`${API_BASE_URL}/api/analytics/funnel?${qs}`, { headers });
  if (!res.ok) throw new Error(`Funnel API error: ${res.status}`);
  return res.json();
}

export async function fetchDrilldown(stage: string, params: DrilldownParams = {}): Promise<DrilldownResponse> {
  if (QA_BYPASS) return { stage, items: [] } as unknown as DrilldownResponse;
  const headers = await getAuthHeader();
  const qs = buildQs(params as Record<string, string | number>);
  const res = await fetch(`${API_BASE_URL}/api/analytics/funnel/${encodeURIComponent(stage)}?${qs}`, { headers });
  if (!res.ok) throw new Error(`Drilldown API error: ${res.status}`);
  return res.json();
}

export async function fetchBottleneck(params: FunnelParams = {}): Promise<BottleneckResponse> {
  if (QA_BYPASS) return QA_BOTTLENECK;
  const headers = await getAuthHeader();
  const qs = buildQs(params as Record<string, string>);
  const res = await fetch(`${API_BASE_URL}/api/analytics/bottleneck?${qs}`, { headers });
  if (!res.ok) throw new Error(`Bottleneck API error: ${res.status}`);
  return res.json();
}

export async function fetchSummary(params: FunnelParams = {}): Promise<SummaryApiResponse> {
  if (QA_BYPASS) return QA_SUMMARY;
  const headers = await getAuthHeader();
  const qs = buildQs(params as Record<string, string>);
  const res = await fetch(`${API_BASE_URL}/api/analytics/summary?${qs}`, { headers });
  if (!res.ok) throw new Error(`Summary API error: ${res.status}`);
  return res.json();
}

export async function fetchDashboardConfig(): Promise<DashboardConfig> {
  if (QA_BYPASS) return QA_DASHBOARD_CONFIG;
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/analytics/dashboard-config`, { headers });
  if (!res.ok) throw new Error(`Dashboard config API error: ${res.status}`);
  return res.json();
}

export async function fetchDiagnostic(params: FunnelParams = {}): Promise<DiagnosticResponse> {
  if (QA_BYPASS) return QA_DIAGNOSTIC;
  const headers = await getAuthHeader();
  const qs = buildQs(params as Record<string, string>);
  const res = await fetch(`${API_BASE_URL}/api/analytics/diagnostic?${qs}`, { headers });
  if (!res.ok) throw new Error(`Diagnostic API error: ${res.status}`);
  return res.json();
}
